import React, { useEffect, useRef, useState } from "react";
import "./landingpage.css";
import { replace, useNavigate } from "react-router-dom";
import { BASE_URL } from "../config/config";
import axios from "axios";
import ScrollReveal from "../common/scrollReveal/ScrollReveal";

const LineChart = ({ data, color = "#b6874f" }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const draw = () => {
      const w = Math.max(el.clientWidth || 560, 280);
      const h = 220, pad = 28;
      const max = Math.max(...data) * 1.15;
      const min = Math.min(0, Math.min(...data));
      const stepX = (w - pad * 1.4) / (data.length - 1);
      const scaleY = (v) => h - pad * 0.6 - ((v - min) / (max - min)) * (h - pad * 1.2);
      const pts = data.map((v, i) => [pad + i * stepX, scaleY(v)]);
      const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
      const baseY = h - pad * 0.6;
      const area = `${line} L${pts[pts.length - 1][0].toFixed(1)},${baseY} L${pts[0][0].toFixed(1)},${baseY} Z`;

      el.innerHTML = `
        <svg viewBox="0 0 ${w} ${h}" width="100%" height="${h}" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="terra-line-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="${color}" stop-opacity="0.35"/>
              <stop offset="100%" stop-color="${color}" stop-opacity="0"/>
            </linearGradient>
          </defs>
          ${[0,1,2,3].map(i => {
            const y = pad * 0.4 + i * (h - pad) / 3;
            return `<line x1="${pad}" y1="${y.toFixed(1)}" x2="${w - pad * 0.4}" y2="${y.toFixed(1)}" stroke="rgba(255,255,255,0.06)" stroke-width="1"/>`;
          }).join("")}
          <path d="${area}" fill="url(#terra-line-gradient)"/>
          <path d="${line}" fill="none" stroke="${color}" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"/>
          ${pts.map(p => `<circle cx="${p[0].toFixed(1)}" cy="${p[1].toFixed(1)}" r="3" fill="#fdfbf5" stroke="${color}" stroke-width="2"/>`).join("")}
        </svg>`;
    };

    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(el);
    return () => ro.disconnect();
  }, [data, color]);

  return <div ref={ref} className="terra-line-chart" />;
};

const DonutChart = ({ data }) => {
  const size = 160, stroke = 20, r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let offset = 0;

  return (
    <div className="terra-donut-chart">
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} aria-hidden="true">
        {data.map((item, index) => {
          const fraction = item.value / total;
          const dash = fraction * circumference;
          const dashOffset = -offset * circumference;
          offset += fraction;
          return (
            <circle
              key={index}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={item.color}
              strokeWidth={stroke}
              strokeDasharray={`${dash.toFixed(1)} ${circumference.toFixed(1)}`}
              strokeDashoffset={dashOffset.toFixed(1)}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          );
        })}
      </svg>
    </div>
  );
};

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate()
const [properties, setProperties] = useState([]);  

  useEffect(() => {
    document.title = "Fractional Real Estate Investment";
    const elements = document.querySelectorAll(".reveal");

    if (!("IntersectionObserver" in window)) {
      elements.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.14 });

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const closeMenu = () => setMenuOpen(false);
  const handleDiscover = (event) => {
    event.preventDefault();
    document.querySelector("#opportunities")?.scrollIntoView({ behavior: "smooth" });
  };

 const getAllProperties = async () => {
  try {
    const token = localStorage.getItem("rwatoken");

    const response = await axios.get(
      `${BASE_URL}/investor/properties/list`);

    console.log("Properties API response:", response?.data);

    const data = response?.data;

    // Handles direct array response
    if (Array.isArray(data)) {
      setProperties(data);
      return;
    }

    // Handles common wrapped API responses
    if (Array.isArray(data?.properties)) {
      setProperties(data.properties);
      return;
    }

    if (Array.isArray(data?.data)) {
      setProperties(data.data);
      return;
    }

    setProperties([]);
  } catch (error) {
    console.log(
      "Error fetching investment properties:",
      error?.response?.data || error
    );

    setProperties([]);
  }
};
  useEffect(()=>{
    getAllProperties()
  },[])

  const PropertyCard = ({ property, onViewInvestment, activeTab,onSeeDetails }) => {
  const totalBlocks = Number(property?.total_tokens || 0);
  const blocksSold = Number(property?.blocks_sold || 0);

  const availableBlocks = Math.max(totalBlocks - blocksSold, 0);

  const fundedPercentage = Math.min(
    Math.max(
      Number(
        property?.funding_pct ??
          (totalBlocks ? (blocksSold / totalBlocks) * 100 : 0)
      ),
      0
    ),
    100
  );

  const imagePath = property?.image_paths?.[0];

  const imageUrl = imagePath
    ? `${BASE_URL}/uploads/${imagePath}`
    : null;

  const propertyType =
    property?.property_type?.replace(/_/g, " ") || "Property";

  const location = property?.location || "Location unavailable";

  const status = property?.status || property?.submission_status || "Open";

  const statusText = status
    .toString()
    .replace(/_/g, " ")
    .toLowerCase();

  const isClosingSoon = statusText.includes("closing");

  return (
    <div
      className="
        group relative overflow-hidden
        rounded-[18px]
        border border-[#e7e5d8]
        bg-[#fffdf8]/95
        shadow-[0_8px_24px_rgba(55,92,32,0.08)]
        backdrop-blur-xl
        transition-all duration-300
        hover:-translate-y-1
        hover:border-[#cbd8bb]
        hover:shadow-[0_18px_38px_rgba(55,92,32,0.15)]
      "
    >
      <div
        className="
          pointer-events-none absolute
          -right-12 -top-12
          h-28 w-28
          rounded-full
          bg-[#e3ecd6]/70
          blur-[35px]
        "
      />

      <div className="relative m-2 overflow-hidden rounded-[13px]">
        <div className="h-[165px] w-full overflow-hidden bg-[#f1eee4]">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={property?.name || "Property"}
              className="
                h-full w-full
                object-cover
                transition-transform duration-500
                group-hover:scale-[1.06]
              "
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[#7b95a5]">
              No image available
            </div>
          )}
        </div>

        <div className="absolute inset-x-0 top-0 flex items-start justify-between p-2">
          <span
            className="
              rounded-md
              border border-white/60
              bg-[#375c20]/90
              px-2.5 py-1
              text-[10px]
              font-semibold
              capitalize
              tracking-wide
              text-white
              shadow-sm
              backdrop-blur-md
            "
          >
            {propertyType}
          </span>

          <span
            className="
              rounded-md
              border border-white/70
              bg-[#fffdf8]/95
              px-2.5 py-1
              text-[10px]
              font-bold
              text-[#4c7a2e]
              shadow-sm
            "
          >
            {property?.gross_annual_yield_pct ?? 0}% yield
          </span>
        </div>
      </div>

      <div className="px-3 pb-3">

        <div className="mb-1 flex items-center gap-1.5">
          <svg
            className="h-3 w-3 shrink-0 text-[#6a7847]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 21s7-6.2 7-12a7 7 0 10-14 0c0 5.8 7 12 7 12z"
            />
            <circle cx="12" cy="9" r="2.2" />
          </svg>

          <span
            className="
              truncate
              text-[13px]
              font-medium
              text-[#6b6d5d]
            "
            title={location}
          >
            {location}
          </span>
        </div>

        <h3
          className="
            truncate
            text-[20px]
            font-bold
            leading-tight
            text-[#20231a]
          "
          title={property?.name}
        >
          {property?.name || "Unnamed Property"}
        </h3>

        <p className="mt-0.5 text-[12px] capitalize text-[#7b7d6d]">
          {propertyType}
        </p>

        <p
          className="
            mt-1.5
            line-clamp-1
            text-[12px]
            leading-4
            text-[#777967]
          "
        >
          {property?.description || "Premium investment opportunity"}
        </p>

        <div
          className="
            mt-2.5
            grid grid-cols-3
            divide-x divide-[#e0e2d7]
            rounded-[10px]
            border border-[#e2e3d9]
            bg-[#f7f4eb]
            px-1 py-2
          "
        >
          <div className="px-2">
            <p className="text-[11px] font-medium text-[#7b7d6d]">
              Property value
            </p>

            <p className="mt-0.5 truncate text-[14px] font-bold text-[#20231a]">
              AED {Number(property?.property_value_aed || 0).toLocaleString()}
            </p>
          </div>

          <div className="px-2">
            <p className="text-[11px] font-medium text-[#7b7d6d]">
              Min. investment
            </p>

            <p className="mt-0.5 truncate text-[14px] font-bold text-[#20231a]">
              AED
              {Number(
                property?.price_per_block_aed || 0
              ).toLocaleString()}
            </p>
          </div>

          <div className="px-2">
            <p className="text-[11px] font-medium text-[#7b7d6d]">
              Target yield
            </p>

            <p className="mt-0.5 text-[14px] font-bold text-[#24719a]">
              {property?.gross_annual_yield_pct ?? 0}%
            </p>
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between">
          <div>
            <p className="text-[11px] text-[#858676]">
              Total token
            </p>

            <p className="text-[14px] font-semibold text-[#4f5545]">
              {totalBlocks.toLocaleString()}
            </p>
          </div>

          <div className="text-right">
            <p className="text-[11px] text-[#858676]">
              Available
            </p>

            <p className="text-[14px] font-semibold text-[#4f5545]">
              {availableBlocks.toLocaleString()}
            </p>
          </div>
        </div>

        <div className="mt-2">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-[11px] font-medium text-[#5d604f]">
              {Math.round(fundedPercentage)}% funded
            </span>

            <span className="text-[11px] text-[#777967]">
              {blocksSold.toLocaleString()} sold
            </span>
          </div>

          <div className="h-[4px] overflow-hidden rounded-full bg-[#e5e7dc]">
            <div
              className="
                h-full
                rounded-full
                bg-gradient-to-r
                from-[#6fa142]
                to-[#375c20]
                transition-all duration-500
              "
              style={{
                width: `${fundedPercentage}%`,
              }}
            />
          </div>
        </div>

        <div className="mt-2.5 flex items-center justify-between">

          <span
            className={`
              rounded-full
              px-2 py-1
              text-[11px]
              font-semibold
              capitalize
              ${
                isClosingSoon
                  ? "bg-[#f6eddc] text-[#8b6a34]"
                  : "bg-[#e3ecd6] text-[#4f6e39]"
              }
            `}
          >
            {statusText}
          </span>

          <button
            type="button"
            onClick={() => {
              if(activeTab === "my-properties"){
                onSeeDetails(property)
              }else{
                onViewInvestment(property)}
              }
            }
            className="
              rounded-full
              bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20]
              px-5 py-1.5
              text-[16px]
              font-semibold
              text-white
              shadow-[0_6px_14px_rgba(55,92,32,0.20)]
              transition-all duration-200
              hover:brightness-95
              hover:shadow-[0_9px_20px_rgba(55,92,32,0.28)]
              active:scale-95
            "
          >
            {activeTab === "my-properties" ? "See Details" : "Invest"}
          </button>
        </div>
      </div>
    </div>
  );
};
const scrollToSection = (sectionId) => {
  closeMenu();

  setTimeout(() => {
    const section = document.querySelector(sectionId);

    if (section) {
      section.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, 100);
};
  return (
    <main className="terra-home w-full max-w-[1800]">
    {/* ============ NAV ============ */}
    <header className="nav">
      <div className="container">
        <a href="#home" className="brand">
          <span className="brand-mark">
            T
          </span>
        </a>
        <nav className="nav-links" >
          <a href="#opportunities">
            Explore Properties
          </a>
          <a href="#categories">
            Investment Opportunities
          </a>
          <a href="#how-it-works">
            How It Works
          </a>
          <a href="#why-terra">
            About
          </a>
        </nav>
        <div className="nav-actions">
          <a href="#login" className="link-login" onClick={()=>navigate("/login", { replace: true })}>
            Log in
          </a>
          <a href="#signup-investor" className="btn btn-primary" onClick={()=>navigate("/login", { replace: true })}>
            Invest Now
          </a>
        </div>
        <button type="button" className={`nav-toggle ${menuOpen ? "is-open" : ""}`} aria-label="Menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((value) => !value)}>
          <span></span>
        </button>
      </div>
    </header>
    
    <div
  className={`mobile-panel ${
    menuOpen ? "is-open" : ""
  }`}
>
  <div className="mobile-menu-inner">

    {/* Menu Header */}
    <div className="mobile-menu-header">
      <div>
        <span className="mobile-menu-eyebrow">
          REAL ESTATE
        </span>

        <h3>
          Explore GT
        </h3>
      </div>

      <button
        type="button"
        className="mobile-menu-close"
        onClick={closeMenu}
        aria-label="Close menu"
      >
        ×
      </button>
    </div>

    {/* Navigation */}
    <div className="mobile-menu-links">

      <button
        type="button"
        onClick={() => scrollToSection("#opportunities")}
        className="mobile-menu-item"
      >
        <span className="mobile-menu-item-number">
          01
        </span>

        <span className="mobile-menu-item-content">
          <strong>
            Explore Properties
          </strong>

          <small>
            Discover available investments
          </small>
        </span>

        <span className="mobile-menu-arrow">
          →
        </span>
      </button>

      <button
        type="button"
         onClick={() => scrollToSection("#categories")}
        className="mobile-menu-item"
      >
        <span className="mobile-menu-item-number">
          02
        </span>

        <span className="mobile-menu-item-content">
          <strong>
            Investment Opportunities
          </strong>

          <small>
            Browse property categories
          </small>
        </span>

        <span className="mobile-menu-arrow">
          →
        </span>
      </button>

      <button
        type="button"
        onClick={() => scrollToSection("#how-it-works")}
        className="mobile-menu-item"
      >
        <span className="mobile-menu-item-number">
          03
        </span>

        <span className="mobile-menu-item-content">
          <strong>
            How It Works
          </strong>

          <small>
            Understand the investment process
          </small>
        </span>

        <span className="mobile-menu-arrow">
          →
        </span>
      </button>

      <button
        type="button"
        onClick={() => scrollToSection("#why-terra")}
        className="mobile-menu-item"
      >
        <span className="mobile-menu-item-number">
          04
        </span>

        <span className="mobile-menu-item-content">
          <strong>
            About
          </strong>

          <small>
            Learn more about Terra
          </small>
        </span>

        <span className="mobile-menu-arrow">
          →
        </span>
      </button>

    </div>

    {/* Bottom Actions */}
    <div className="mobile-menu-actions">

      <button
        type="button"
       onClick={()=>navigate("/login", { replace: true })}
        className="mobile-login-button"
      >
        Log in
      </button>

      <button
        type="button"
        onClick={()=>navigate("/login", { replace: true })}
        className="mobile-invest-button"
      >
        <span>
          Invest Now
        </span>

        <span>
          →
        </span>
      </button>

      <button
        type="button"
        onClick={()=>navigate("/login", { replace: true })}
        className="mobile-list-button"
      >
        List Your Property
      </button>

    </div>

  </div>
</div>
    {/* ============ HERO ============ */}
    <section className="hero">
      <div className="container">
        <div className="hero-copy">
          <span className="hero-eyebrow">
            Fractional real estate, from AED 5000
          </span>
          <h1>
            Invest in real estate. Build your future.
          </h1>
          <p className="lede">
            We let you own a share of vetted, income-producing properties — no mortgage, no property management, no six-figure minimum. Browse the opportunity, invest in minutes, track your returns from one dashboard.
          </p>
          <div className="hero-ctas">
            <a href="#opportunities" className="btn btn-primary btn-lg">
              Explore Properties
            </a>
            <a href="#signup-owner" onClick={()=>navigate("/login")} className="btn btn-ghost btn-lg">
              List Your Property
            </a>
          </div>
          <div className="hero-trust">
            <div className="stat">
              <b>
                AED 142M+
              </b>
              <span>
                Total funded value
              </span>
            </div>
            <div className="stat">
              <b>
                2,800+
              </b>
              <span>
                Active investors
              </span>
            </div>
            <div className="stat">
              <b>
                8.4%
              </b>
              <span>
                Avg. target yield
              </span>
            </div>
          </div>
        </div>
        <div className="hero-visual">
          <div className="frame frame-main">
            <img src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1000&q=80" alt="Modern investment villa at dusk" />
          </div>
          <div className="frame frame-accent">
            <img src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=700&q=80" alt="Contemporary property exterior" />
          </div>
          <div className="glass-card gc-1">
            <div className="g-label">
              Starting investment
            </div>
            <div className="g-value">
              AED 5000
            </div>
          </div>
          <div className="glass-card gc-2">
            <div className="g-label">
              Expected return
            </div>
            <div className="g-value">
              8.5%
            </div>
          </div>
          <div className="glass-card gc-3">
            <div className="g-label">
              Property value
            </div>
            <div className="g-value">
              AED 750K
            </div>
          </div>
        </div>
      </div>
    </section>
    {/* ============ DISCOVER / SEARCH ============ */}
    <div className="container discover">
      <form className="discover-panel" data-discover-form="" onSubmit={handleDiscover}>
        <div className="field">
          <label htmlFor="f-loc">
            Location
          </label>
          <select id="f-loc">
            <option>
              Any location
            </option>
            <option>
              Austin, TX
            </option>
            <option>
              Miami, FL
            </option>
            <option>
              Denver, CO
            </option>
            <option>
              Dubai, UAE
            </option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="f-type">
            Property type
          </label>
          <select id="f-type">
            <option>
              Any type
            </option>
            <option>
              Residential
            </option>
            <option>
              Commercial
            </option>
            <option>
              Luxury
            </option>
            <option>
              Multifamily
            </option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="f-range">
            Investment range
          </label>
          <select id="f-range">
            <option>
              Any amount
            </option>
            <option>
              AED 5000 – AED 2,0000
            </option>
            <option>
              AED 2,0000 – AED 10,0000
            </option>
            <option>
              AED 10,000+
            </option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="f-yield">
            Expected return
          </label>
          <select id="f-yield">
            <option>
              Any yield
            </option>
            <option>
              6% – 8%
            </option>
            <option>
              8% – 10%
            </option>
            <option>
              10%+
            </option>
          </select>
        </div>
        <button type="submit" className="btn btn-primary">
          Search
        </button>
      </form>
    </div>
    {/* ============ FEATURED OPPORTUNITIES ============ */}
    <section className="section" id="opportunities">
      <div className="container">
        <div className="section-head reveal">
          <div>
            <span className="eyebrow">
              Investment opportunities
            </span>
            <h2>
              Featured properties raising capital now
            </h2>
          </div>
          <p>
            Every listing is underwritten by our team before it opens for investment — financials, title, and projected yield included.
          </p>
        </div>
       <div className="property-grid">
  {properties.length > 0 ? (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
  {properties?.map((property) => (
    <ScrollReveal key={property?.id}>
      <PropertyCard
        property={property}
        onViewInvestment={() => navigate("/login")}
        activeTab="customer"
        onSeeDetails={() => navigate("/login")}
      />
    </ScrollReveal>
  ))}
</div>
  ) : (
    <div className="property-empty">
      <div className="property-empty-icon">
        🏠
      </div>

      <h3>No properties available</h3>

      <p>
        There are currently no investment properties available.
      </p>
    </div>
  )}
</div>
        {/* <div className="grid">
          <section className="pcard reveal">
            <div className="pcard-media">
              <img src="https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=700&q=80" alt="Modern Villa in Austin" />
              <span className="pcard-tag">
                Villa
              </span>
              <span className="pcard-yield">
                8.5% yield
              </span>
              <div className="pcard-overlay">
                4 bed · 3 bath · 2,400 sqft — funded by 214 investors
              </div>
            </div>
            <div className="pcard-body">
              <div className="pcard-loc">
                Austin, Texas
              </div>
              <h3 className="pcard-name">
                Modern Villa
              </h3>
              <p className="pcard-type">
                Single-family residential
              </p>
              <div className="pcard-metrics">
                <div>
                  <div className="m-label">
                    Property value
                  </div>
                  <div className="m-value">
                    AED 250K
                  </div>
                </div>
                <div>
                  <div className="m-label">
                    Min. investment
                  </div>
                  <div className="m-value">
                    AED 500k
                  </div>
                </div>
                <div>
                  <div className="m-label">
                    Target yield
                  </div>
                  <div className="m-value">
                    8.5%
                  </div>
                </div>
              </div>
              <div className="pcard-progress">
                <div className="bar">
                  <span style={{ width: '64%' }}></span>
                </div>
                <div className="pcard-progress-meta">
                  <span>
                    64% funded
                  </span>
                  <span>
                    214 investors
                  </span>
                </div>
              </div>
              <div className="pcard-foot">
                <span className="badge badge-green">
                  Open
                </span>
                <a href="#property-details" onClick={()=>navigate("/login")} className="btn btn-primary">
                  Invest
                </a>
              </div>
            </div>
          </section>
          <article className="pcard reveal">
            <div className="pcard-media">
              <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=700&q=80" alt="Heights apartment complex" />
              <span className="pcard-tag">
                Apartment
              </span>
              <span className="pcard-yield">
                7.2% yield
              </span>
              <div className="pcard-overlay">
                32 units — stabilized, income-producing since 2019
              </div>
            </div>
            <div className="pcard-body">
              <div className="pcard-loc">
                Denver, Colorado
              </div>
              <h3 className="pcard-name">
                Heights Apartments
              </h3>
              <p className="pcard-type">
                Multifamily
              </p>
              <div className="pcard-metrics">
                <div>
                  <div className="m-label">
                    Property value
                  </div>
                  <div className="m-value">
                    AED 4.1M
                  </div>
                </div>
                <div>
                  <div className="m-label">
                    Min. investment
                  </div>
                  <div className="m-value">
                    AED 1,000
                  </div>
                </div>
                <div>
                  <div className="m-label">
                    Target yield
                  </div>
                  <div className="m-value">
                    7.2%
                  </div>
                </div>
              </div>
              <div className="pcard-progress">
                <div className="bar">
                  <span style={{ width: '41%' }}></span>
                </div>
                <div className="pcard-progress-meta">
                  <span>
                    41% funded
                  </span>
                  <span>
                    96 investors
                  </span>
                </div>
              </div>
              <div className="pcard-foot">
                <span className="badge badge-green">
                  Open
                </span>
                <a href="#property-details" className="btn btn-primary">
                  Invest
                </a>
              </div>
            </div>
          </article>
          <article className="pcard reveal">
            <div className="pcard-media">
              <img src="https://images.unsplash.com/photo-1571939228382-b2f2b585ce15?auto=format&fit=crop&w=700&q=80" alt="Downtown skyline tower" />
              <span className="pcard-tag">
                Commercial
              </span>
              <span className="pcard-yield">
                9.1% yield
              </span>
              <div className="pcard-overlay">
                Class-A office floor, 92% leased
              </div>
            </div>
            <div className="pcard-body">
              <div className="pcard-loc">
                Miami, Florida
              </div>
              <h3 className="pcard-name">
                Bayview Tower — Floor 12
              </h3>
              <p className="pcard-type">
                Commercial office
              </p>
              <div className="pcard-metrics">
                <div>
                  <div className="m-label">
                    Property value
                  </div>
                  <div className="m-value">
                    AED 6.8M
                  </div>
                </div>
                <div>
                  <div className="m-label">
                    Min. investment
                  </div>
                  <div className="m-value">
                    AED 2,500
                  </div>
                </div>
                <div>
                  <div className="m-label">
                    Target yield
                  </div>
                  <div className="m-value">
                    9.1%
                  </div>
                </div>
              </div>
              <div className="pcard-progress">
                <div className="bar">
                  <span style={{ width: '78%' }}></span>
                </div>
                <div className="pcard-progress-meta">
                  <span>
                    78% funded
                  </span>
                  <span>
                    301 investors
                  </span>
                </div>
              </div>
              <div className="pcard-foot">
                <span className="badge badge-amber">
                  Closing soon
                </span>
                <a href="#property-details" className="btn btn-primary">
                  Invest
                </a>
              </div>
            </div>
          </article>
        </div> */}
      </div>
    </section>
    {/* ============ CATEGORIES ============ */}
    <section className="section section-sand" id="categories">
      <div className="container">
        <div className="section-head reveal">
          <div>
            <span className="eyebrow">
              Browse by category
            </span>
            <h2>
              Every kind of property, one platform
            </h2>
          </div>
          <p>
            Diversify across residential, commercial, and development deals without managing a single one yourself.
          </p>
        </div>
        <div className="cat-grid">
          <a className="cat-card reveal" href="#opportunities">
            <img src="https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=700&q=80" alt="Residential homes" />
            <div className="cat-card-label">
              <span className="c-count">
                48 opportunities
              </span>
              <h3>
                Residential
              </h3>
            </div>
          </a>
          <a className="cat-card reveal" href="#opportunities">
            <img src="https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&w=700&q=80" alt="Commercial building" />
            <div className="cat-card-label">
              <span className="c-count">
                21 opportunities
              </span>
              <h3>
                Commercial
              </h3>
            </div>
          </a>
          <a className="cat-card reveal" href="#opportunities">
            <img src="https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=700&q=80" alt="Luxury interior" />
            <div className="cat-card-label">
              <span className="c-count">
                16 opportunities
              </span>
              <h3>
                Luxury
              </h3>
            </div>
          </a>
          <a className="cat-card reveal" href="#opportunities">
            <img src="https://images.unsplash.com/photo-1449844908441-8829872d2607?auto=format&fit=crop&w=700&q=80" alt="Vacation home with pool" />
            <div className="cat-card-label">
              <span className="c-count">
                12 opportunities
              </span>
              <h3>
                Vacation Homes
              </h3>
            </div>
          </a>
          <a className="cat-card reveal" href="#opportunities">
            <img src="https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?auto=format&fit=crop&w=700&q=80" alt="Multifamily building" />
            <div className="cat-card-label">
              <span className="c-count">
                27 opportunities
              </span>
              <h3>
                Multifamily
              </h3>
            </div>
          </a>
          <a className="cat-card reveal" href="#opportunities">
            <img src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=700&q=80" alt="Development project site" />
            <div className="cat-card-label">
              <span className="c-count">
                9 opportunities
              </span>
              <h3>
                Development Projects
              </h3>
            </div>
          </a>
        </div>
      </div>
    </section>
    {/* ============ HOW IT WORKS ============ */}
    <section className="section" id="how-it-works">
      <div className="container">
        <div className="section-head reveal">
          <div>
            <span className="eyebrow">
              Process
            </span>
            <h2>
              From browsing to owning, in four steps
            </h2>
          </div>
          <p>
            No paperwork marathon. Most investors complete their first investment in under ten minutes.
          </p>
        </div>
        <div className="steps">
          <div className="step reveal">
            <div className="s-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"></circle>
                <path d="M21 21l-4.3-4.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"></path>
              </svg>
            </div>
            <h3>
              Discover
            </h3>
            <p>
              Filter properties by location, type, minimum investment, and target yield.
            </p>
          </div>
          <div className="step reveal">
            <div className="s-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M4 19h16M4 15l4-5 4 3 5-7 3 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
              </svg>
            </div>
            <h3>
              Analyze
            </h3>
            <p>
              Review underwriting, financials, comparable sales, and projected returns.
            </p>
          </div>
          <div className="step reveal">
            <div className="s-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 3v18M5 8l7-5 7 5M5 8v11h14V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
              </svg>
            </div>
            <h3>
              Invest
            </h3>
            <p>
              Buy fractional shares starting at AED 5000, funded securely in a few clicks.
            </p>
          </div>
          <div className="step reveal">
            <div className="s-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 2v4M12 18v4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M2 12h4M18 12h4M4.9 19.1l2.8-2.8M16.3 7.7l2.8-2.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"></path>
              </svg>
            </div>
            <h3>
              Earn
            </h3>
            <p>
              Receive distributions and track appreciation from your investor dashboard.
            </p>
          </div>
        </div>
      </div>
    </section>
    {/* ============ WHY TERRA ============ */}
    <section className="section section-sand" id="why-terra">
      <div className="container">
        <div className="section-head reveal">
          <div>
            <span className="eyebrow">
              Why We
            </span>
            <h2>
              Built for people who want real estate returns without the overhead
            </h2>
          </div>
          <p>
            We handle sourcing, underwriting, legal structure, and property management. You own the upside.
          </p>
        </div>
        <div className="benefit-grid">
          <div className="benefit reveal">
            <h3>
              Fractional ownership
            </h3>
            <p>
              Own a real, recorded share of a property — starting at AED 5000, not AED 50,000.
            </p>
          </div>
          <div className="benefit reveal">
            <h3>
              Diversified opportunities
            </h3>
            <p>
              Spread capital across residential, commercial, and multifamily deals in one portfolio.
            </p>
          </div>
          <div className="benefit reveal">
            <h3>
              Transparent information
            </h3>
            <p>
              Full financials, title, and inspection reports available before you invest any Amount.
            </p>
          </div>
          <div className="benefit reveal">
            <h3>
              Data-driven insights
            </h3>
            <p>
              Every listing is scored against comparable properties and market trends.
            </p>
          </div>
          <div className="benefit reveal">
            <h3>
              Secure transactions
            </h3>
            <p>
              Bank-level encryption and escrow-backed funding for every investment.
            </p>
          </div>
          <div className="benefit reveal">
            <h3>
              Portfolio tracking
            </h3>
            <p>
              Watch distributions, appreciation, and total return update in real time.
            </p>
          </div>
        </div>
      </div>
    </section>
    {/* ============ DASHBOARD PREVIEW ============ */}
    <section className="section section-dark">
      <div className="container">
        <div className="section-head reveal">
          <div>
            <span className="eyebrow">
              Your portfolio
            </span>
            <h2>
              One dashboard for everything you own
            </h2>
          </div>
          <p>
            Track performance across every property, in real time, from any device.
          </p>
        </div>
        <div className="dash-panel reveal">
          <div className="dash-grid" style={{ marginBottom: '1.8rem' }}>
            <div className="kpi">
              <div className="k-label">
                Total invested
              </div>
              <div className="k-value">
                AED 48,200
              </div>
              <div className="k-delta up">
                +AED 4,100 this quarter
              </div>
            </div>
            <div className="kpi">
              <div className="k-label">
                Portfolio value
              </div>
              <div className="k-value">
                AED 53,760
              </div>
              <div className="k-delta up">
                +11.9% overall
              </div>
            </div>
            <div className="kpi">
              <div className="k-label">
                Annual return
              </div>
              <div className="k-value">
                8.7%
              </div>
              <div className="k-delta up">
                vs. 6.2% avg. REIT
              </div>
            </div>
            <div className="kpi">
              <div className="k-label">
                Monthly income
              </div>
              <div className="k-value">
                AED 412
              </div>
              <div className="k-delta up">
                Paid on the 1st
              </div>
            </div>
          </div>
          <div className="dash-grid-2">
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
                <span style={{ fontSize: '0.82rem', color: 'rgba(250,247,239,0.6)' }}>
                  Portfolio value — 12 months
                </span>
              </div>
              <LineChart data={[38200,39750,41100,40800,43250,44900,46200,47500,48900,50300,52100,53760]} />
            </div>
            <div>
              <div style={{ fontSize: '0.82rem', color: 'rgba(250,247,239,0.6)', marginBottom: '0.8rem' }}>
                Allocation by type
              </div>
              <div style={{ display: 'flex', gap: '1.2rem', alignItems: 'center' }}>
                <DonutChart data={[{ value: 42, color: "#d7ac78" }, { value: 31, color: "#8a9463" }, { value: 27, color: "#6a7847" }]} />
                <div>
                  <div className="legend-row" style={{ color: 'rgba(250,247,239,0.75)' }}>
                    <span className="legend-dot" style={{ background: '#d7ac78' }}></span>
                    Residential — 42%
                  </div>
                  <div className="legend-row" style={{ color: 'rgba(250,247,239,0.75)' }}>
                    <span className="legend-dot" style={{ background: '#8a9463' }}></span>
                    Multifamily — 31%
                  </div>
                  <div className="legend-row" style={{ color: 'rgba(250,247,239,0.75)' }}>
                    <span className="legend-dot" style={{ background: '#6a7847' }}></span>
                    Commercial — 27%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    {/* ============ EDITORIAL FEATURE ============ */}
    <section className="section">
      <div className="container">
        <div className="feature-editorial reveal">
          <img src="https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?auto=format&fit=crop&w=1400&q=80" alt="The Oak Residence exterior" />
          <div className="feature-content">
            <span className="eyebrow">
              Editor's pick
            </span>
            <h2>
              The Oak Residence
            </h2>
            <p>
              A fully renovated 5-bedroom estate on two private acres, leased through 2027 to a long-term tenant with a corporate guarantee.
            </p>
            <div className="feature-metrics">
              <div>
                <b>
                  AED 750,000
                </b>
                <span>
                  Property value
                </span>
              </div>
              <div>
                <b>
                  8.2%
                </b>
                <span>
                  Target return
                </span>
              </div>
              <div>
                <b>
                  Austin, TX
                </b>
                <span>
                  Location
                </span>
              </div>
            </div>
            <a href="#opportunities" className="btn btn-secondary">
              Explore Investment
            </a>
          </div>
        </div>
      </div>
    </section>
    {/* ============ TESTIMONIALS ============ */}
    <section className="section section-sand">
      <div className="container">
        <div className="section-head reveal">
          <div>
            <span className="eyebrow">
              Investor trust
            </span>
            <h2>
              What our investors say
            </h2>
          </div>
        </div>
        <div className="testi-grid">
          <div className="testi reveal">
            <p className="quote">
              "I put in AED 2,000 across three properties as a test. Eighteen months later it's the most transparent investment I own — I can see the lease, the financials, everything."
            </p>
            <div className="testi-person">
              <img className="testi-avatar" src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=100&q=80" alt="" />
              <div>
                <b>
                  Maria Chen
                </b>
                <span>
                  Retail investor
                </span>
              </div>
            </div>
          </div>
          <div className="testi reveal">
            <p className="quote">
              "Listing our duplex on our funded in 11 days. The underwriting process was more rigorous than I expected, which honestly made me trust the platform more."
            </p>
            <div className="testi-person">
              <img className="testi-avatar" src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=100&q=80" alt="" />
              <div>
                <b>
                  David Okafor
                </b>
                <span>
                  Property owner
                </span>
              </div>
            </div>
          </div>
          <div className="testi reveal">
            <p className="quote">
              "Monthly distributions land like clockwork. It's the closest thing to owning rental property without ever fixing a leaky faucet."
            </p>
            <div className="testi-person">
              <img className="testi-avatar" src="https://images.unsplash.com/photo-1547425260-76bcadfb4f2c?auto=format&fit=crop&w=100&q=80" alt="" />
              <div>
                <b>
                  Priya Ramesh
                </b>
                <span>
                  Retail investor
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    {/* ============ FINAL CTA ============ */}
    <section className="section">
      <div className="container">
        <div className="final-cta reveal">
          <h2>
            Your next investment starts here.
          </h2>
          <p>
            Join thousands of investors building wealth through fractional real estate — or list your property and reach them in days.
          </p>
          <div className="hero-ctas">
            <a href="#opportunities" className="btn btn-primary btn-lg">
              Explore Opportunities
            </a>
            <a href="#signup-owner" className="btn btn-ghost btn-lg" onClick={()=>navigate("/login")}>
              List Your Property
            </a>
          </div>
        </div>
      </div>
    </section>
    {/* ============ FOOTER ============ */}
    <footer className="site-footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-brand">
            <a href="#home" className="brand" style={{ color: 'var(--sand-050)' }}>
              <span className="brand-mark">
                T
              </span>
              
            </a>
            <p>
              Fractional real estate investing, built on transparency and rigorous underwriting.
            </p>
          </div>
          <div>
            <h4>
              Investment
            </h4>
            <ul>
              <li>
                <a href="#properties">
                  Browse properties
                </a>
              </li>
              <li>
                <a href="#signup-investor">
                  How investing works
                </a>
              </li>
              <li>
                <a href="#why-terra">
                  Why US
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4>
              Properties
            </h4>
            <ul>
              <li>
                <a href="#properties">
                  All listings
                </a>
              </li>
              <li>
                <a href="#signup-owner">
                  List a property
                </a>
              </li>
              <li>
                <a href="#categories">
                  Categories
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4>
              Resources
            </h4>
            <ul>
              <li>
                <a href="#how-it-works">
                  How it works
                </a>
              </li>
              <li>
                <a href="#">
                  Help center
                </a>
              </li>
              <li>
                <a href="#">
                  Investor guide
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4>
              Company
            </h4>
            <ul>
              <li>
                <a href="#">
                  About
                </a>
              </li>
              <li>
                <a href="#">
                  Careers
                </a>
              </li>
              <li>
                <a href="#">
                  Contact
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <span>
            © 2026 Real Estate, Inc. All rights reserved.
          </span>
          <span>
            <a href="#">
              Privacy
            </a>
            ·
            <a href="#">
              Terms
            </a>
          </span>
          <div className="social-row">
            <a href="#" aria-label="X">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M4 4l16 16M20 4L4 20" stroke="currentColor" strokeWidth="1.6"></path>
              </svg>
            </a>
            <a href="#" aria-label="LinkedIn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="1.6"></rect>
              </svg>
            </a>
            <a href="#" aria-label="Instagram">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6"></circle>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
    </main>
  );
}
